import { Component, Input, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { PosDiscountPlanService } from 'src/app/services/pos-discount-plan.service';
import { SYNCFUSION_MODULES, ANGULAR_MODULES } from 'src/app/Shared/shared-imports';
import { DecimalPipe } from '@angular/common';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { VendorService } from 'src/app/services/Vendor.service';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-vendor-pos-discount-plans',
  standalone: true,
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, PercentageFormatterDirective, WordReplacePipe, PhoneFormatPipe, LoaderComponent],
  templateUrl: './ops-vendor-pos-discount-plans.html',
  styleUrl: './ops-vendor-pos-discount-plans.scss',
  providers: [DecimalPipe]
})
export class OpsVendorPosDiscountPlans implements OnInit, OnDestroy {
  @Input() vendorId!: number;
  @Output() cancelAndNavigate = new EventEmitter<string>();

  discountPlanForm!: FormGroup;
  allData: any[] = [];
  selectedPlanId: number | null = null;
  private destroy$ = new Subject<void>();

  today: Date = new Date();
  minExpiryDate: Date = new Date();
  vendorDiscountAll: boolean = false;
  vendorDiscountAllPercent = 0;
  isGridDisabled = false;
  vendorInfo: any;
  loaderIcon: Promise<boolean>;

  days = [
    { field: 'Sun', label: 'Sun' },
    { field: 'Mon', label: 'Mon' },
    { field: 'Tue', label: 'Tue' },
    { field: 'Wed', label: 'Wed' },
    { field: 'Thu', label: 'Thu' },
    { field: 'Fri', label: 'Fri' },
    { field: 'Sat', label: 'Sat' }
  ];

  constructor(
    private fb: FormBuilder,
    private discountPlanService: PosDiscountPlanService,
    private toastr: ToastrService,
    private decimalPipe: DecimalPipe,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private vendorService: VendorService
  ) { }

  ngOnInit(): void {
    this.getVendorInfo();
    this.initForm();

    this.discountPlanForm.get('DiscountAll')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((checked: boolean) => {
        const percentControl = this.discountPlanForm.get('DiscountAllPercent');
        if (checked) {
          percentControl?.enable({ emitEvent: false });
        } else {
          percentControl?.reset(0, { emitEvent: false });
          percentControl?.disable({ emitEvent: false });
        }

        this.toggleIndividualPlanControls(checked);
        this.isGridDisabled = checked;
      });

    this.discountPlanForm.get('All')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((checked: boolean) => {
        if (checked) {
          this.days.forEach(day => {
            this.discountPlanForm.get(day.field)?.setValue(true, { emitEvent: false });
          });
        }
      });

    this.days.forEach(day => {
      this.discountPlanForm.get(day.field)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const allChecked = this.days.every(d => this.discountPlanForm.get(d.field)?.value === true);
          if (!allChecked) {
            this.discountPlanForm.get('All')?.setValue(false, { emitEvent: false });
          }
        });
    });

    this.discountPlanForm.get('EffectiveFrom')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((start: Date) => {
        if (start) {
          this.minExpiryDate = new Date(start);
          const expiry = this.discountPlanForm.get('ExpiryDate')?.value;

          if (expiry && new Date(expiry) < new Date(start)) {
            this.discountPlanForm.get('ExpiryDate')?.setValue(null);
          }
        }
      });

    if (this.vendorId) {
      this.loadDiscountPlans();
    }
  }

  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) this.vendorInfo = res;
      });
  }

  onStartDateChange(event: any): void {
    const startDate = event.value;
    if (startDate) {
      this.minExpiryDate = new Date(startDate);
    }
  }

  trimAllTextFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.trimAllTextFields(control);
      } else {
        if (typeof control?.value === 'string') {
          control.setValue(control.value.trim());
        }
      }
    });
  }

  private initForm(): void {
    this.discountPlanForm = this.fb.group({
      DiscountAll: [false],
      DiscountAllPercent: [{ value: 0, disabled: true }, [Validators.min(0), Validators.max(100)]],
      PlanId: [{ value: null, disabled: true }],
      PlanName: ['', [Validators.required, Validators.maxLength(50)]],
      EffectiveFrom: [this.today, Validators.required],
      ExpiryDate: [this.today, Validators.required],
      DiscountPercent: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      Sun: [false],
      Mon: [false],
      Tue: [false],
      Wed: [false],
      Thu: [false],
      Fri: [false],
      Sat: [false],
      All: [false]
    });
  }

  private toggleIndividualPlanControls(isDisabled: boolean): void {
    const fieldsToToggle = [
      'PlanName', 'EffectiveFrom', 'ExpiryDate', 'DiscountPercent', 'All',
      'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'
    ];

    fieldsToToggle.forEach(field => {
      const control = this.discountPlanForm.get(field);
      if (control) {
        isDisabled ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
      }
    });
  }

  private formatPercentageField(controlName: string) {
    const el = document.querySelector<HTMLInputElement>(`input[formcontrolname="${controlName}"]`);
    const value = this.discountPlanForm.get(controlName)?.value || 0;
    if (el) {
      el.value = (this.decimalPipe.transform(value, '1.2-4') || '0.0000') + '%';
    }
  }

  private loadDiscountPlans(): void {
    this.loaderIcon = Promise.resolve(true);
    this.discountPlanService.getPOSDiscountPlans(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plans: any) => {
          this.allData = plans?.Plans || [];
          this.vendorDiscountAll = plans?.DiscountAll ?? false;
          this.vendorDiscountAllPercent = plans?.DiscountAllPercent ?? 0;

          setTimeout(() => {
            this.formatPercentageField('DiscountPercent');
            this.formatPercentageField('DiscountAllPercent');
          });

          this.resetForm(true);
          this.loaderIcon = Promise.resolve(false);
        },
        error: () => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error(
            $localize`Error loading discount plans`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          )
        }
      });
  }

  addDiscountPlan(): void {
    this.trimAllTextFields(this.discountPlanForm);

    if (this.discountPlanForm.invalid) {
      this.discountPlanForm.markAllAsTouched();
      this.toastr.warning(
        $localize`Please fill all required fields`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const planName = this.discountPlanForm.get('PlanName')?.value?.trim();
    if (!planName) {
      this.toastr.warning(
        $localize`Plan name is required`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const duplicatePlan = this.allData.find(p =>
      p.PlanName?.trim().toLowerCase() === planName.toLowerCase() &&
      p.PlanId !== this.selectedPlanId
    );

    if (duplicatePlan) {
      this.toastr.warning(
        $localize`A plan with the name '${planName}' already exists.`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const discountPercent = this.discountPlanForm.get('DiscountPercent')?.value || 0;
    if (discountPercent <= 0) {
      this.toastr.warning(
        $localize`Discount percentage must be greater than 0`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const isAnyDayChecked = this.days.some(
      d => this.discountPlanForm.get(d.field)?.value === true
    );
    if (!isAnyDayChecked) {
      this.toastr.warning(
        $localize`Please select at least one weekday for which the discount is applicable!.`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const plan: any = {
      ...this.discountPlanForm.getRawValue(),
      PlanId: this.selectedPlanId || (this.allData.length + 1),
      VendorId: this.vendorId
    };

    if (this.selectedPlanId) {
      const idx = this.allData.findIndex(p => p.PlanId === this.selectedPlanId);
      if (idx !== -1) {
        this.allData[idx] = { ...plan };
      }
    } else {
      if (this.allData.length >= 5) {
        this.toastr.warning(
          $localize`You have discount plans on 5 categories already! Can't add a new category.`,
          $localize`Validation`,
          this.sharedHTMLSettings.ToasterConfig
        );
        return;
      }
      this.allData.push(plan);
    }

    this.allData = this.allData.map((p, index) => ({
      ...p,
      PlanId: index + 1
    }));

    this.allData = [...this.allData];

    this.resetForm();
  }

  deleteDiscountPlan(planId: number): void {
    Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`Do you want to delete this discount plan?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Delete`,
      cancelButtonText: $localize`Cancel`
    }).then(result => {
      if (result.isConfirmed) {
        this.allData = this.allData
          .filter(p => p.PlanId !== planId)
          .map((p, index) => ({ ...p, PlanId: index + 1 }));

        this.allData = [...this.allData];
        this.resetForm();
      }
    });
  }

  saveDiscountPlans(): void {
    const discountAllChecked = this.discountPlanForm.get('DiscountAll')?.value;
    const discountAllPercent = Number(this.discountPlanForm.get('DiscountAllPercent')?.value || 0);

    if (discountAllChecked && (isNaN(discountAllPercent) || discountAllPercent <= 0)) {
      this.toastr.warning(
        $localize`Please enter a valid discount % for 'Apply Discount on all items on all days'`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const request = {
      VendorId: this.vendorId,
      DiscountAll: discountAllChecked,
      DiscountAllPercent: discountAllPercent,
      Plans: this.allData.map(p => ({
        ...p,
        EffectiveFrom: this.toDateOnly(p.EffectiveFrom),
        ExpiryDate: this.toDateOnly(p.ExpiryDate)
      }))
    };

    Swal.fire({
      title: $localize`Save All Discount Plans?`,
      text: $localize`This will update all discount plans in the database.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Save`,
      cancelButtonText: $localize`Cancel`
    }).then(result => {
      if (result.isConfirmed) {
        this.discountPlanService.savePOSDiscountPlans(request)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res?.Success) {
                this.toastr.success(
                  $localize`Discount plans saved successfully`,
                  $localize`Success`,
                  this.sharedHTMLSettings.ToasterConfig
                );
                this.loadDiscountPlans();
              } else {
                this.toastr.error(
                  res?.Message || $localize`Error saving plans`,
                  $localize`Error`,
                  this.sharedHTMLSettings.ToasterConfig
                );
              }
            },
            error: () => this.toastr.error(
              $localize`Error saving plans`,
              $localize`Error`,
              this.sharedHTMLSettings.ToasterConfig
            )
          });
      }
    });
  }

  toDateOnly(date: any): string | null {
    if (!date) return null;

    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  onRowSelected(args: any): void {
    if (this.isGridDisabled) return;
    const rowData: any = args.data;
    if (!rowData) return;

    this.selectedPlanId = rowData.PlanId ?? null;
    this.discountPlanForm.patchValue({
      PlanId: rowData.PlanId,
      PlanName: rowData.PlanName,
      EffectiveFrom: rowData.EffectiveFrom,
      ExpiryDate: rowData.ExpiryDate,
      DiscountPercent: rowData.DiscountPercent,
      Sun: rowData.Sun,
      Mon: rowData.Mon,
      Tue: rowData.Tue,
      Wed: rowData.Wed,
      Thu: rowData.Thu,
      Fri: rowData.Fri,
      Sat: rowData.Sat,
      All: rowData.All
    });
    this.discountPlanForm.markAsPristine();

    setTimeout(() => {
      this.formatPercentageField('DiscountPercent');
      this.formatPercentageField('DiscountAllPercent');
    });
  }

  private resetForm(fromLoad: boolean = false): void {
    const discountAllValue = fromLoad
      ? this.vendorDiscountAll
      : this.discountPlanForm.get('DiscountAll')?.value;

    const discountAllPercentValue = fromLoad
      ? this.vendorDiscountAllPercent
      : this.discountPlanForm.get('DiscountAllPercent')?.value;

    this.discountPlanForm.reset({
      DiscountAll: discountAllValue,
      DiscountAllPercent: discountAllPercentValue,
      PlanId: { value: this.allData.length < 5 ? this.allData.length + 1 : null, disabled: true },
      PlanName: '',
      EffectiveFrom: this.today,
      ExpiryDate: this.today,
      DiscountPercent: 0,
      Sun: false,
      Mon: false,
      Tue: false,
      Wed: false,
      Thu: false,
      Fri: false,
      Sat: false,
      All: false
    });

    this.discountPlanForm.markAsPristine();
    this.selectedPlanId = null;

    this.toggleIndividualPlanControls(!!discountAllValue);
    this.isGridDisabled = !!discountAllValue;

    setTimeout(() => {
      this.formatPercentageField('DiscountPercent');
      this.formatPercentageField('DiscountAllPercent');
    });
  }

  cancelEdit(): void {
    this.resetForm();
  }

  onCancel(): void {
    if (this.discountPlanForm.dirty) {
      Swal.fire({
        title: 'Discard Changes?',
        text: 'You have unsaved changes. Do you really want to cancel?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }).then((result) => {
        if (result.isConfirmed) {
          this.discountPlanForm.reset();
          this.cancelAndNavigate.emit('vendorInfo');
        }
      });
    } else {
      this.cancelAndNavigate.emit('vendorInfo');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
